<!--
  PolicyPage.vue -- a store's hosted Privacy / Refund policy page.

  Jake, 2026-10-19: a store's policy can be shown as a rich-text page on the
  store itself, at /shop/<slug>/privacy-policy and /shop/<slug>/refund-policy,
  and "they should look like they're part of the store website, not some
  separate, orphaned page."

  That is done by REUSING the storefront shell rather than drawing one:

    * The route carries meta.layout = 'storefront', so StorefrontLayout renders
      the SAME top bar as every other store page (branding image, the centered
      store title linking to the store home, the top-right controls).
    * It mounts under StorefrontSessionGate like Home / Category / Search, so
      the shopper session, the password gate and the preview skip apply
      identically -- a policy page is not a way around the store's gate.
    * It loads the same chrome (useStorefrontChrome commits brand color, store
      name and logo to Vuex for that top bar) and the same footer.
    * The category nav is NOT mounted here. StorefrontLayout renders it ONCE,
      pinned under the header, for every storefront page; a second bar here
      (plus a "Home > <policy>" breadcrumb) made "Home" show three times above
      the policy (Bug Intake #456). The h1 is the page's title.

  The page TITLE (the h1 and document.title) is the policy's label
  ("Privacy Policy" unless the store renamed it).

  SECURITY: the body is distributor-authored HTML. It is sanitized by the API
  on write and on read and AGAIN here (useStorefrontPolicyPage ->
  sanitizePolicyHtml); the only v-html in this file binds that sanitized
  string. The label is bound with {{ }} (text).

  ONE not-found state: unknown store, a store that links to its own URL, and
  empty content all 404 identically.

  Preview-safe: plain reads only.
-->
<template>
  <div class="sf-policy" data-testid="sf-policy-page">
    <div class="sf-policy__body sf-container">
      <div
        v-if="loading || chromeLoading"
        class="sf-policy__status"
        data-testid="sf-policy-loading"
      >
        Loading. Please wait...
      </div>

      <div
        v-else-if="error"
        class="sf-policy__status"
        data-testid="sf-policy-error"
      >
        <template v-if="error.status === 404">
          <h1 class="sf-policy__title" data-testid="sf-policy-notfound-title">
            Page not found
          </h1>
          <p data-testid="sf-policy-notfound">
            This store does not have that page.
          </p>
          <router-link :to="homePath" data-testid="sf-policy-home-link"
            >Back to the store</router-link
          >
        </template>
        <template v-else>{{ error.message }}</template>
      </div>

      <article v-else-if="page" class="sf-policy__article">
        <h1 class="sf-policy__title" data-testid="sf-policy-title">
          {{ page.label }}
        </h1>
        <!-- SECURITY: page.html is the sanitizePolicyHtml output. -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          class="sf-policy__content"
          data-testid="sf-policy-content"
          v-html="page.html"
        />
      </article>
    </div>

    <storefront-footer
      :policies="policies"
      :store-slug="slug"
      :white-label="whiteLabel"
    />
  </div>
</template>

<script>
import { resolveStorefrontSlug } from "@/router/storefrontSlug";
import { reapplyStorefrontChrome } from "@/router/storefrontBrowserChrome";
import { useStorefrontChrome } from "@/composables/useStorefrontChrome";
import { useStorefrontPolicyPage } from "@/composables/useStorefrontPolicyPage";

import StorefrontFooter from "@/components/Storefront/StorefrontFooter.vue";

export default {
  name: "StorefrontPolicyPage",

  components: {
    "storefront-footer": StorefrontFooter,
  },

  setup() {
    const {
      loading: chromeLoading,
      fetchChrome,
      campaignId,
      policies,
      whiteLabel,
      error: chromeError,
    } = useStorefrontChrome();
    const { page, loading, error: pageError, fetchPolicy } =
      useStorefrontPolicyPage();

    return {
      chromeLoading,
      fetchChrome,
      campaignId,
      policies,
      whiteLabel,
      chromeError,
      page,
      loading,
      pageError,
      fetchPolicy,
    };
  },

  data() {
    return { titleApplied: false };
  },

  computed: {
    slug() {
      return resolveStorefrontSlug(this.$route) || "";
    },

    // "privacy" | "refund", declared by the route.
    kind() {
      return this.$route?.meta?.policyKind || "";
    },

    homePath() {
      return `/shop/${this.slug}`;
    },

    // A store that does not resolve is the same not-found as a missing page.
    error() {
      if (this.chromeError && this.chromeError.status === 404) {
        return { status: 404, message: "Page not found." };
      }
      return this.pageError;
    },

    routeKey() {
      return `${this.slug}::${this.kind}`;
    },
  },

  watch: {
    routeKey: {
      immediate: true,
      handler() {
        this.loadAll();
      },
    },

    // The body is read by store id, which the chrome fetch resolves. Also
    // re-read when the same store's other policy route is opened.
    campaignId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchPolicy(id, this.kind);
      },
    },
    kind() {
      if (this.campaignId) this.fetchPolicy(this.campaignId, this.kind);
    },

    // document.title = "<label> - <store name>" once the page is known.
    page() {
      this.applyTitle();
    },
  },

  beforeUnmount() {
    // Hand the tab back to the chrome hook, which computes the right title
    // for wherever the visitor is now. Restoring a captured string could bring
    // back a previous store's title after a store switch.
    if (this.titleApplied) reapplyStorefrontChrome();
  },

  methods: {
    loadAll() {
      if (!this.slug) return;
      this.fetchChrome(this.slug);
    },

    applyTitle() {
      if (!this.page) return;
      this.titleApplied = true;
      // The name counts only if it was committed for THIS page's store
      // (see storefrontBrowserChrome.js: a stale name from another store must
      // never reach the tab).
      const getters = this.$store?.getters || {};
      const sameStore =
        typeof getters.getStoreSlug === "string" &&
        getters.getStoreSlug.toLowerCase() === this.slug.toLowerCase();
      const storeName = sameStore ? String(getters.getStoreName || "").trim() : "";
      document.title = storeName
        ? `${this.page.label} - ${storeName}`
        : this.page.label;
    },
  },
};
</script>

<style scoped>
/* Sticky footer, same contract as CategoryPage / SearchPage -- see
   CategoryPage's style block for why this is min-height:100%. */
.sf-policy {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.sf-policy > .sf-footer {
  margin-top: auto;
}

.sf-policy__body {
  padding: 8px 32px 48px;
}

.sf-policy__article {
  /* A readable measure for long legal copy. */
  max-width: 800px;
  margin: 0 auto;
}

.sf-policy__title {
  margin: 16px 0 16px;
  font-size: 28px;
  font-weight: 600;
  color: var(--tf-fg-1, #212121);
  overflow-wrap: anywhere;
}

.sf-policy__status {
  padding: 32px;
  text-align: center;
  color: var(--tf-fg-3, #616161);
}

.sf-policy__content {
  font-size: 16px;
  line-height: 1.65;
  color: var(--tf-fg-2, #333);
  overflow-wrap: anywhere;
}

.sf-policy__content :deep(h2) {
  margin: 28px 0 8px;
  font-size: 22px;
  font-weight: 600;
}
.sf-policy__content :deep(h3) {
  margin: 22px 0 6px;
  font-size: 18px;
  font-weight: 600;
}
.sf-policy__content :deep(h4) {
  margin: 18px 0 4px;
  font-size: 16px;
  font-weight: 600;
}
.sf-policy__content :deep(p) {
  margin: 0 0 14px;
}
.sf-policy__content :deep(ul),
.sf-policy__content :deep(ol) {
  margin: 0 0 14px;
  padding-left: 28px;
}
.sf-policy__content :deep(blockquote) {
  margin: 0 0 14px;
  padding: 4px 16px;
  border-left: 3px solid var(--tf-border, #d1d5db);
  color: var(--tf-fg-3, #616161);
}
.sf-policy__content :deep(hr) {
  margin: 24px 0;
  border: 0;
  border-top: 1px solid var(--tf-border, #d1d5db);
}
.sf-policy__content :deep(a) {
  color: var(--tf-primary, #1565c0);
  text-decoration: underline;
}

@media (max-width: 600px) {
  .sf-policy__body {
    padding: 8px 16px 40px;
  }
  .sf-policy__title {
    font-size: 24px;
  }
}
</style>
